/** Confirmation for the mode switches that also disable / restore the legacy
 *  vendor webhooks, and for the standalone "Disable webhooks" action. Lists
 *  exactly the webhooks the action touches, because a disable is a DELETE at
 *  the vendor (it has no "disabled" state). */
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import type { LegacyWebhook } from '@/api/notificationSubscriptions';
import { AppDialog } from '@/components/AppDialog';

export type LegacyWebhookAction = 'process' | 'log_only' | 'disable';

const mono = { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' } as const;

interface Props {
  action: LegacyWebhookAction | null;
  vendor: string;
  /** The webhooks the action deletes (process / disable) or re-creates (log_only). */
  webhooks: LegacyWebhook[];
  /** Managed but not restorable: kept by a disable, listed so nothing surprises. */
  kept: LegacyWebhook[];
  onConfirm: (action: LegacyWebhookAction) => void;
  onCancel: () => void;
}

const COPY: Record<LegacyWebhookAction, { title: string; confirm: string }> = {
  process: { title: 'Switch to process & disable webhooks?', confirm: 'Switch & disable webhooks' },
  log_only: { title: 'Switch to log only & enable webhooks?', confirm: 'Enable webhooks & switch' },
  disable: { title: 'Disable the legacy webhooks?', confirm: 'Disable webhooks' },
};

export function LegacyWebhookSwitchDialog({
  action,
  vendor,
  webhooks,
  kept,
  onConfirm,
  onCancel,
}: Props) {
  const copy = action ? COPY[action] : COPY.process;
  const deletes = action !== 'log_only';
  return (
    <AppDialog
      open={action !== null}
      onClose={onCancel}
      title={copy.title}
      actions={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="contained"
            color={deletes ? 'error' : 'primary'}
            onClick={() => action && onConfirm(action)}
          >
            {copy.confirm}
          </Button>
        </>
      }
    >
      <Stack spacing={1.5}>
        {action === 'process' && (
          <Typography variant="body2">
            Deliveries on this subscription will feed the import pipeline from the next notification
            on. The mode switches first; then the webhooks below are disabled.
          </Typography>
        )}
        {action === 'log_only' && (
          <Typography variant="body2">
            These webhooks are re-created at {vendor} (they get new ids) before the subscription
            returns to log only. If any of them fails, the mode is not changed — the subscription
            keeps processing so updates keep arriving.
          </Typography>
        )}
        {deletes && (
          <Alert severity="warning">
            {vendor} has no &quot;disabled&quot; state — these webhooks will be <b>deleted</b> at{' '}
            {vendor}. Their full configuration is kept on this subscription and restored by
            &quot;Switch to log only &amp; enable webhooks&quot;.
          </Alert>
        )}
        <WebhookList webhooks={webhooks} />
        {deletes && kept.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary">
              Kept ({vendor} masked their header values, so they could not be restored):
            </Typography>
            <WebhookList webhooks={kept} />
          </>
        )}
      </Stack>
    </AppDialog>
  );
}

function WebhookList({ webhooks }: { webhooks: LegacyWebhook[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }} aria-label="Affected webhooks">
      {webhooks.map((w) => (
        <li key={w.id}>
          <Box component="span" sx={mono}>
            {w.url}
          </Box>{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            ({w.id})
          </Typography>
        </li>
      ))}
    </Box>
  );
}
